import INewAddress from "../models/INewAddress";
import { countries as allCountries } from "./CountriesConstService";

/**
 * AddressParseService
 *
 * Client-side heuristic parser that turns a block of pasted free-text address into the structured
 * INewAddress fields used throughout the app. It is intentionally best-effort: the PasteAddressModal
 * presents the result in editable inputs so the user can correct anything the heuristics get wrong.
 *
 * Only emits the keys it can confidently fill. The `country` value is an ISO cca3 code ("USA", "CAN")
 * to match the country <select> which is built from CountriesConstService with `value: cca3`.
 */

const US_ZIP_REGEX = /\b\d{5}(?:-\d{4})?\b/;
const CA_POSTAL_REGEX = /\b[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d\b/;

// Street suffixes / unit markers used to detect address lines.
const STREET_HINT_REGEX =
    /\b(\d+|st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|ct|court|cir|circle|way|pl|place|pkwy|parkway|hwy|highway|ste|suite|unit|apt|apartment|fl|floor|po box|p\.o\. box|bldg|building|rm|room|#)\b/i;

/**
 * Maps a US state / Canadian province name or code to its 2-letter code.
 * Returns the uppercased code, or null if not recognized.
 */
function normalizeStateCode(raw: string): string | null {
    const value = raw.trim().toUpperCase();

    switch (value) {
        // US states
        case "AL":
        case "ALABAMA":
            return "AL";
        case "AK":
        case "ALASKA":
            return "AK";
        case "AZ":
        case "ARIZONA":
            return "AZ";
        case "AR":
        case "ARKANSAS":
            return "AR";
        case "CA":
        case "CALIFORNIA":
            return "CA";
        case "CO":
        case "COLORADO":
            return "CO";
        case "CT":
        case "CONNECTICUT":
            return "CT";
        case "DE":
        case "DELAWARE":
            return "DE";
        case "DC":
        case "DISTRICT OF COLUMBIA":
            return "DC";
        case "FL":
        case "FLORIDA":
            return "FL";
        case "GA":
        case "GEORGIA":
            return "GA";
        case "HI":
        case "HAWAII":
            return "HI";
        case "ID":
        case "IDAHO":
            return "ID";
        case "IL":
        case "ILLINOIS":
            return "IL";
        case "IN":
        case "INDIANA":
            return "IN";
        case "IA":
        case "IOWA":
            return "IA";
        case "KS":
        case "KANSAS":
            return "KS";
        case "KY":
        case "KENTUCKY":
            return "KY";
        case "LA":
        case "LOUISIANA":
            return "LA";
        case "ME":
        case "MAINE":
            return "ME";
        case "MD":
        case "MARYLAND":
            return "MD";
        case "MA":
        case "MASSACHUSETTS":
            return "MA";
        case "MI":
        case "MICHIGAN":
            return "MI";
        case "MN":
        case "MINNESOTA":
            return "MN";
        case "MS":
        case "MISSISSIPPI":
            return "MS";
        case "MO":
        case "MISSOURI":
            return "MO";
        case "MT":
        case "MONTANA":
            return "MT";
        case "NE":
        case "NEBRASKA":
            return "NE";
        case "NV":
        case "NEVADA":
            return "NV";
        case "NH":
        case "NEW HAMPSHIRE":
            return "NH";
        case "NJ":
        case "NEW JERSEY":
            return "NJ";
        case "NM":
        case "NEW MEXICO":
            return "NM";
        case "NY":
        case "NEW YORK":
            return "NY";
        case "NC":
        case "NORTH CAROLINA":
            return "NC";
        case "ND":
        case "NORTH DAKOTA":
            return "ND";
        case "OH":
        case "OHIO":
            return "OH";
        case "OK":
        case "OKLAHOMA":
            return "OK";
        case "OR":
        case "OREGON":
            return "OR";
        case "PA":
        case "PENNSYLVANIA":
            return "PA";
        case "RI":
        case "RHODE ISLAND":
            return "RI";
        case "SC":
        case "SOUTH CAROLINA":
            return "SC";
        case "SD":
        case "SOUTH DAKOTA":
            return "SD";
        case "TN":
        case "TENNESSEE":
            return "TN";
        case "TX":
        case "TEXAS":
            return "TX";
        case "UT":
        case "UTAH":
            return "UT";
        case "VT":
        case "VERMONT":
            return "VT";
        case "VA":
        case "VIRGINIA":
            return "VA";
        case "WA":
        case "WASHINGTON":
            return "WA";
        case "WV":
        case "WEST VIRGINIA":
            return "WV";
        case "WI":
        case "WISCONSIN":
            return "WI";
        case "WY":
        case "WYOMING":
            return "WY";

        // Canadian provinces / territories
        case "AB":
        case "ALBERTA":
            return "AB";
        case "BC":
        case "BRITISH COLUMBIA":
            return "BC";
        case "MB":
        case "MANITOBA":
            return "MB";
        case "NB":
        case "NEW BRUNSWICK":
            return "NB";
        case "NL":
        case "NEWFOUNDLAND AND LABRADOR":
            return "NL";
        case "NS":
        case "NOVA SCOTIA":
            return "NS";
        case "NT":
        case "NORTHWEST TERRITORIES":
            return "NT";
        case "NU":
        case "NUNAVUT":
            return "NU";
        case "ON":
        case "ONTARIO":
            return "ON";
        case "PE":
        case "PRINCE EDWARD ISLAND":
            return "PE";
        case "QC":
        case "QUEBEC":
            return "QC";
        case "SK":
        case "SASKATCHEWAN":
            return "SK";
        case "YT":
        case "YUKON":
            return "YT";

        default:
            return null;
    }
}

const CA_PROVINCE_CODES = new Set(["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"]);

/**
 * Normalizes an explicit country name/code (last-line) to an ISO cca3 code, or null if unrecognized.
 */
function normalizeCountryCode(raw: string): string | null {
    const value = raw.trim().toUpperCase();

    switch (value) {
        case "US":
        case "USA":
        case "U.S.":
        case "U.S.A.":
        case "UNITED STATES":
        case "UNITED STATES OF AMERICA":
            return "USA";
        case "CA":
        case "CAN":
        case "CANADA":
            return "CAN";
        default:
            break;
    }

    // Fall back to matching the common country name from CountriesConstService.
    const match = allCountries.find((country) => country.name?.common?.toUpperCase() === value);
    return match ? match.cca3 : null;
}

function looksLikeStreet(line: string): boolean {
    return STREET_HINT_REGEX.test(line);
}

/**
 * Parses the "city, ST ZIP" style line into its parts. Returns null if no postal code is found.
 */
function parseCityStateZip(line: string): { city?: string; state?: string; postal_code?: string } | null {
    const usZip = line.match(US_ZIP_REGEX);
    const caPostal = line.match(CA_POSTAL_REGEX);

    if (!usZip && !caPostal) {
        return null;
    }

    const result: { city?: string; state?: string; postal_code?: string } = {};

    if (usZip) {
        result.postal_code = usZip[0];
    } else if (caPostal) {
        result.postal_code = caPostal[0];
    }

    // Strip the postal code, then look at the remaining "city, state" portion.
    let remainder = line.replace(US_ZIP_REGEX, "").replace(CA_POSTAL_REGEX, "").trim();
    // Drop a trailing comma left from stripping the zip.
    remainder = remainder.replace(/[,\s]+$/, "");

    // Split on comma first (City, State), else fall back to last whitespace token as the state.
    let cityPart = remainder;
    let statePart = "";

    if (remainder.includes(",")) {
        const parts = remainder.split(",");
        statePart = parts.pop()?.trim() || "";
        cityPart = parts.join(",").trim();
    } else if (normalizeStateCode(remainder)) {
        // The whole remainder is just a state (e.g. "IL" or "Ontario") — the city lives elsewhere.
        statePart = remainder;
        cityPart = "";
    } else {
        const tokens = remainder.split(/\s+/);
        if (tokens.length > 1) {
            statePart = tokens.pop() || "";
            cityPart = tokens.join(" ");
        }
    }

    const stateCode = normalizeStateCode(statePart);
    if (stateCode) {
        result.state = stateCode;
    } else if (statePart) {
        // Keep whatever was there so the user can correct it in review.
        result.state = statePart;
    }

    if (cityPart) {
        result.city = cityPart;
    }

    return result;
}

/**
 * Parses a block of free-text address into structured INewAddress fields (best-effort).
 */
export function parseAddress(raw: string): Partial<INewAddress> {
    const result: Partial<INewAddress> = {};

    if (!raw || !raw.trim()) {
        return result;
    }

    // Split on newlines; if it's a single line, fall back to comma separation.
    let lines = raw
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

    let commaMode = false;
    if (lines.length === 1) {
        commaMode = true;
        lines = lines[0]
            .split(",")
            .map((part) => part.trim())
            .filter((part) => part.length > 0);
    }

    if (lines.length === 0) {
        return result;
    }

    // Identify the city/state/zip line (the one containing a recognizable postal code).
    let cityStateZipIndex = -1;
    for (let i = lines.length - 1; i >= 0; i--) {
        const parsed = parseCityStateZip(lines[i]);
        if (parsed) {
            cityStateZipIndex = i;
            Object.assign(result, parsed);
            break;
        }
    }

    // In comma-mode the city is usually its own fragment immediately before "ST ZIP"
    // (e.g. "Toronto, ON M5H 2N2" → ["Toronto", "ON M5H 2N2"]). Pull it in when the
    // state/zip fragment didn't already supply a city.
    let cityConsumedIndex = -1;
    if (commaMode && cityStateZipIndex > 0 && !result.city) {
        const candidate = lines[cityStateZipIndex - 1];
        if (!looksLikeStreet(candidate)) {
            result.city = candidate;
            cityConsumedIndex = cityStateZipIndex - 1;
        }
    }

    // Detect an explicit trailing country line (after the city/state/zip line).
    let countryIndex = -1;
    for (let i = lines.length - 1; i > cityStateZipIndex && cityStateZipIndex !== -1; i--) {
        const code = normalizeCountryCode(lines[i]);
        if (code) {
            result.country = code;
            countryIndex = i;
            break;
        }
    }

    // Infer country from the detected state / postal format if not explicitly provided.
    if (!result.country) {
        if (result.state && CA_PROVINCE_CODES.has(result.state)) {
            result.country = "CAN";
        } else if (result.postal_code && CA_POSTAL_REGEX.test(result.postal_code)) {
            result.country = "CAN";
        } else if (result.state || result.postal_code) {
            result.country = "USA";
        }
    }

    // Everything before the city/state/zip line is name / company / street lines.
    const upperBound = cityStateZipIndex === -1 ? lines.length : cityStateZipIndex;
    const headLines: string[] = [];
    const streetLines: string[] = [];

    for (let i = 0; i < upperBound; i++) {
        if (i === countryIndex || i === cityConsumedIndex) {
            continue;
        }

        const line = lines[i];
        if (looksLikeStreet(line)) {
            streetLines.push(line);
        } else if (streetLines.length === 0) {
            // Non-street lines before any street line are treated as name/company.
            headLines.push(line);
        } else {
            // A non-street line after a street line (e.g. an unlabeled suite) still belongs to the address.
            streetLines.push(line);
        }
    }

    if (headLines.length > 0) {
        result.name = headLines[0];
    }
    if (headLines.length > 1) {
        result.company_name = headLines.slice(1).join(", ");
    }

    if (streetLines[0]) {
        result.address_line_one = streetLines[0];
    }
    if (streetLines[1]) {
        result.address_line_two = streetLines[1];
    }
    if (streetLines[2]) {
        result.address_line_three = streetLines.slice(2).join(", ");
    }

    return result;
}
